import { advancedMetricValue, advancedNumber, derivedPlayerMetrics } from './advancedStatistics';
import { playerComparisonMetric, playerKey } from './playerRanking';
import { playerParticipationRates } from './playerStatistics';

const BASIC = ['matches', 'minutes', 'startsPercent', 'minutesPerMatch', 'goals90', 'nonPenaltyGoals90', 'yellow90'];
const ADVANCED = [
  ['rating', 'FS_RATING', 'media'], ['xg90', 'EXPECTED_GOALS', 'por90'],
  ['xa90', 'EXPECTED_ASSISTS', 'por90'], ['assists90', 'ASSISTS_GOAL', 'por90'],
  ['shots90', 'SHOTS_TOTAL', 'por90'], ['shotAccuracy', 'DERIVED_SHOT_ACCURACY', 'porcentaje'],
  ['recoveries90', 'BALL_RECOVERIES', 'por90'], ['interceptions90', 'INTERCEPTIONS', 'por90'],
  ['tackles90', 'TACKLES_WON', 'por90'], ['duelsPercent', 'DUELS_EFFICIENCY', 'porcentaje'],
  ['aerialPercent', 'DUELS_AERIAL_EFFICIENCY', 'porcentaje'], ['passPercent', 'PASSES_ACCURACY', 'porcentaje'],
  ['dribbles90', 'DRIBBLES_WON', 'por90'], ['saves90', 'SAVES_TOTAL', 'por90'],
  ['prevented90', 'GOALS_PREVENTED', 'por90'],
];
export const DISCOVERY_METRICS = [
  ...BASIC.map(id => ({ id, advanced: false })),
  ...ADVANCED.map(([id, field, mode]) => ({ id, field, mode, advanced: true })),
];
export const discoveryMetric = id => DISCOVERY_METRICS.find(metric => metric.id === id);
export const playerPosition = player => {
  for (const value of [player?.demarcacion, player?.posicion, player?.posicionObservada,
    player?.informacionExtra?.posicionesObservadas?.habitual]) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object') {
      for (const label of [value.name, value.nombre, value.position]) {
        if (typeof label === 'string' && label.trim()) return label.trim();
      }
      const flags = [['isGoalkeeper', 'Portero'], ['isDefender', 'Defensa'],
        ['isMidfielder', 'Centrocampista'], ['isForward', 'Delantero']].filter(([key]) => value[key] === true);
      if (flags.length === 1) return flags[0][1];
    }
  }
  return player?.esPortero === true || player?.portero === true ? 'Portero' : '';
};
const normalizedPosition = player => playerPosition(player).normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export function positionFamily(player) {
  const position = normalizedPosition(player);
  if (/^(gk|por|portero|guardameta|goalkeeper)$/.test(position)) return 'goalkeeper';
  if (/^(df|def|defensa|defender|defensor|central|centre.back|center.back|cb|lb|rb|lateral.*|carrilero.*|left.back|right.back)$/.test(position)) return 'defender';
  if (/^(mf|mc|centrocampista|midfielder|mediocentro.*|medio|pivote|interior.*|mediapunta|am|cm|dm|cdm|cam)$/.test(position)) return 'midfielder';
  if (/^(fw|dc|delantero|forward|striker|extremo.*|winger|st|cf|lw|rw)$/.test(position)) return 'forward';
  return null;
}

export const isGoalkeeper = player => player?.esPortero === true || player?.portero === true
  || positionFamily(player) === 'goalkeeper';

export function discoverySample(player, id) {
  const definition = discoveryMetric(id);
  if (!definition) return { value: null, matches: null, minutes: null };
  if (definition.advanced) {
    const scope = player.estadisticasAvanzadas?.total;
    const metric = Object.values(derivedPlayerMetrics(scope)).map(group => group[definition.field]).find(Boolean);
    const matches = advancedNumber(metric?.partidosConDatos);
    const minutes = advancedNumber(metric?.minutosConDatos);
    // Per-90 values require their own measured minutes; the scope total is not their denominator.
    const operation = definition.mode === 'por90' ? 'suma' : definition.mode === 'media' ? 'media' : 'porcentaje';
    const usable = matches > 0 && metric?.operacion === operation && (definition.mode !== 'por90' || minutes > 0);
    return {
      value: usable ? advancedMetricValue(metric, definition.mode) : null,
      matches, minutes,
    };
  }
  const participation = playerParticipationRates(player);
  const { played: matches, minutes } = participation;
  const validCounts = section => section && Object.keys(section).length
    && Object.values(section).filter(value => !Array.isArray(value)).every(value =>
      advancedNumber(value) !== null && Number(value) >= 0);
  const goals90 = key => {
    if (!validCounts(key.startsWith('goles') ? player.goles : player.tarjetas)) return null;
    const count = playerComparisonMetric(player, key);
    return count !== null && minutes > 0 ? 90 * count / minutes : null;
  };
  const values = {
    matches, minutes,
    startsPercent: matches > 0 && participation.starts !== null && participation.starts <= matches
      ? 100 * participation.starts / matches : null,
    minutesPerMatch: participation.minutesPerAppearance,
    goals90: goals90('golesTotales'),
    // The basic writer stores non-penalty goals in golesTotales; penalties are a separate increment.
    nonPenaltyGoals90: validCounts(player.goles) && minutes > 0
      ? 90 * (advancedNumber(player.goles.golesTotales) ?? 0) / minutes : null,
    yellow90: goals90('amarillasTotales'),
  };
  return { value: values[id] ?? null, matches, minutes };
}

export function eligibleDiscoverySample(player, id, { minMetricMinutes = 0, minMetricMatches = 0, source = '' } = {}) {
  const sample = discoverySample(player, id);
  if (!discoveryMetric(id)?.advanced) return sample;
  if ((source && player.estadisticasAvanzadas?.fuente !== source)
    || !player.estadisticasAvanzadas?.fuente
    || !(sample.matches >= Math.max(1, minMetricMatches))
    || (minMetricMinutes > 0 && !(sample.minutes >= minMetricMinutes))) return { ...sample, value: null };
  return sample;
}

export const DISCOVERY_QUERY_KEYS = [
  'minAge', 'maxAge', 'minMatches', 'metricSource', 'metricMinutes', 'metricMatches', 'criteria',
];

export function readDiscoveryFilters(params) {
  let criteria = [];
  let invalid = false;
  if (params.get('criteria')) {
    try {
      const parsed = JSON.parse(params.get('criteria'));
      if (!Array.isArray(parsed) || parsed.length > 8) throw new Error('Invalid criteria');
      invalid = parsed.some(rule => !rule || typeof rule !== 'object' || Array.isArray(rule) || !discoveryMetric(rule?.metric)
        || !['min', 'max'].every(key => rule[key] === '' || rule[key] == null || advancedNumber(rule[key]) !== null)
        || (advancedNumber(rule.min) !== null && advancedNumber(rule.max) !== null && Number(rule.min) > Number(rule.max)));
      criteria = parsed.filter(rule => rule && typeof rule === 'object' && !Array.isArray(rule)).map(rule => ({
        metric: typeof rule.metric === 'string' ? rule.metric : '',
        min: ['string', 'number'].includes(typeof rule.min) ? rule.min : '',
        max: ['string', 'number'].includes(typeof rule.max) ? rule.max : '',
      }));
    } catch { invalid = true; }
  }
  const number = key => {
    const raw = params.get(key);
    if (raw === null || raw === '') return null;
    const value = advancedNumber(raw);
    if (value === null || value < 0) invalid = true;
    return value;
  };
  const minAge = number('minAge'), maxAge = number('maxAge');
  if (minAge !== null && maxAge !== null && minAge > maxAge) invalid = true;
  const minMatches = number('minMatches') ?? 0;
  const minMetricMinutes = number('metricMinutes') ?? 0;
  const minMetricMatches = number('metricMatches') ?? 0;
  return {
    criteria, invalid, minAge, maxAge, minMatches,
    source: params.get('metricSource') || '',
    minMetricMinutes, minMetricMatches,
  };
}

export function matchesDiscoveryFilters(player, filters) {
  if (filters.invalid) return false;
  const age = advancedNumber(player.edad);
  if ((filters.minAge !== null && !(age !== null && age >= filters.minAge))
    || (filters.maxAge !== null && !(age !== null && age <= filters.maxAge))) return false;
  if (filters.minMatches > 0 && !(discoverySample(player, 'matches').value >= filters.minMatches)) return false;
  if (filters.source && player.estadisticasAvanzadas?.fuente !== filters.source) return false;
  return filters.criteria.every(rule => {
    if (!discoveryMetric(rule.metric)) return false;
    const sample = eligibleDiscoverySample(player, rule.metric, filters);
    return sample.value !== null
      && (advancedNumber(rule.min) === null || sample.value >= Number(rule.min))
      && (advancedNumber(rule.max) === null || sample.value <= Number(rule.max));
  });
}

const SIMILARITY_ADVANCED = {
  goalkeeper: ['saves90', 'prevented90', 'passPercent'],
  defender: ['recoveries90', 'interceptions90', 'tackles90', 'aerialPercent', 'duelsPercent', 'passPercent'],
  midfielder: ['recoveries90', 'interceptions90', 'xa90', 'assists90', 'passPercent', 'duelsPercent', 'dribbles90'],
  forward: ['xg90', 'xa90', 'assists90', 'shots90', 'shotAccuracy', 'dribbles90', 'duelsPercent'],
};
const sameContext = (a, b) => ['idTemporada', 'idCompeticion', 'idGrupo'].every(key =>
  a[key] != null && b[key] != null && String(a[key]) === String(b[key]));

export function findSimilarPlayers(target, records, {
  mode = 'basic', positionMode = 'family', minMinutes = 450, minMatches = 3,
} = {}) {
  const empty = reason => ({ reason, results: [], metrics: [], cohortSize: 0 });
  if (!target?.idJugador || !sameContext(target, target)) return empty('context');
  const family = positionFamily(target);
  const position = normalizedPosition(target);
  if (!position || (positionMode === 'family' && !family)) return empty('position');
  const ids = mode === 'advanced' ? SIMILARITY_ADVANCED[family] || [] :
    ['minutesPerMatch', 'startsPercent', 'nonPenaltyGoals90', 'yellow90'];
  const source = mode === 'advanced' ? target.estadisticasAvanzadas?.fuente : null;
  if (mode === 'advanced' && !source) return empty('coverage');
  const settings = { source, minMetricMinutes: minMinutes, minMetricMatches: minMatches };
  const samples = player => ids.map(id => {
    if (mode === 'advanced') return eligibleDiscoverySample(player, id, settings).value;
    const sample = discoverySample(player, id);
    return sample.minutes >= minMinutes && sample.matches >= minMatches ? sample.value : null;
  });
  const targetValues = samples(target);
  const usable = ids.filter((id, index) => targetValues[index] !== null);
  if (usable.length < 3) return empty('coverage');
  const values = player => {
    const all = samples(player);
    return usable.map(id => all[ids.indexOf(id)]);
  };
  const unique = new Map();
  for (const player of records) {
    if (!player.idJugador || String(player.idJugador) === String(target.idJugador) || !sameContext(target, player)
      || (positionMode === 'family' ? positionFamily(player) !== family : normalizedPosition(player) !== position)
      || (source && player.estadisticasAvanzadas?.fuente !== source)) continue;
    const vector = values(player);
    if (vector.some(value => value === null)) continue;
    const key = String(player.idJugador);
    const previous = unique.get(key);
    if (!previous || discoverySample(player, 'minutes').value > discoverySample(previous.player, 'minutes').value) {
      unique.set(key, { player, values: vector });
    }
  }
  const cohort = [{ player: target, values: values(target) }, ...unique.values()];
  if (cohort.length < 3) return empty('cohort');
  const dimensions = usable.map((id, index) => {
    const sorted = cohort.map(item => item.values[index]).sort((a, b) => a - b);
    const ranks = new Map();
    for (let first = 0; first < sorted.length;) {
      let end = first + 1;
      while (end < sorted.length && sorted[end] === sorted[first]) end += 1;
      ranks.set(sorted[first], (first + (end - first - 1) / 2) / (sorted.length - 1));
      first = end;
    }
    return { id, index, ranks };
  }).filter(item => item.ranks.size > 1);
  if (dimensions.length < 3) return empty('variation');
  // Mid-rank percentiles, with one common complete-case cohort and equal weights for every candidate.
  const results = [...unique.values()].map(item => {
    const differences = dimensions.map(({ id, index, ranks }) => ({
      id, target: cohort[0].values[index], candidate: item.values[index],
      distance: Math.abs(ranks.get(cohort[0].values[index]) - ranks.get(item.values[index])),
    })).sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
    const distance = differences.reduce((sum, item) => sum + item.distance, 0) / differences.length;
    return { player: item.player, score: 100 * (1 - distance), differences };
  }).sort((a, b) => b.score - a.score || playerKey(a.player).localeCompare(playerKey(b.player)));
  return { reason: null, results, metrics: dimensions.map(item => item.id), cohortSize: cohort.length };
}

export function playerDiscoveryUrl(player) {
  if (!player?.idJugador || !sameContext(player, player)) return null;
  return `/jugadoresSimilares?${new URLSearchParams({
    similar: playerKey(player), contextSeason: player.idTemporada,
    contextCompetition: player.idCompeticion, contextGroup: player.idGrupo,
  })}`;
}

export function playerComparisonUrl(player) {
  const url = playerDiscoveryUrl(player);
  if (!url) return null;
  const params = new URLSearchParams(url.split('?')[1]);
  params.set('compareA', params.get('similar'));
  params.delete('similar');
  return `/compararJugadores?${params}`;
}
